import { Highlight } from 'components/atoms/inputs/Select/parts/Highlight'
import { Option } from 'components/atoms/inputs/Select/parts/types'
import { Open } from 'components/atoms/inputs/Select/parts/Open'
import { Ref } from 'components/atoms/inputs/Select/parts/Ref'


export type SelectConfig<Value> = {
  options: Option<Value>[]
  value: Value
  onChange?: ((value: Value | null) => void) | null
}

export class Select<Value> {
  options: SelectConfig<Value>['options'] = []
  value: Value | null = null
  private onChange: SelectConfig<Value>['onChange'] = null

  private _render = () => this.render()
  highlight = new Highlight(this._render)
  isOpen = new Open(this._render)

  inputRef = new Ref<HTMLInputElement | null>(null, () => this.setInputValue())

  constructor (
    config: SelectConfig<Value>,
    private render: () => void
  ) {
    this.onChange = config.onChange
    this._patchOptions(config.options)
    this._patchValue(config.value)
  }

  patchOnChange = (onChange: SelectConfig<Value>['onChange']) => {
    if (onChange !== this.onChange) {
      this.onChange = onChange
    }
  }

  patchOptions = (nextOptions: SelectConfig<Value>['options']) => {
    if (this.options === nextOptions) {
      return
    }

    this._patchOptions(nextOptions)
    this.render()
  }

  private _patchOptions = (nextOptions: SelectConfig<Value>['options']) => {
    this.options = nextOptions
    this.highlight.setCount(this.options.length)
  }

  patchValue = (nextValue: Value | null, options: { override?: boolean } = {}) => {
    const isChanged = this.value !== nextValue || !!options.override
    if (!isChanged) {
      return
    }

    const isValueChanged = this._patchValue(nextValue)
    if (!isValueChanged) {
      return
    }

    if (this.onChange) {
      this.onChange(this.value)
    }

    this.render()
  }

  private _patchValue = (nextValue: Value | null) => {
    const index = this.getOptionByValue(nextValue)
    const option = this.options[index] || null
    const nextActualValue = option ? option.value : null
    const isValueChanged = nextActualValue !== this.value
    this.value = nextActualValue
    this.setInputValue()

    return isValueChanged
  }

  private setInputValue () {
    const { current } = this.inputRef
    if (!current) {
      return
    }

    const index = this.getOptionByValue(this.value)
    const option = this.options[index] || null
    const inputValue = option && option.label || ''

    current.value = inputValue
    this.highlight.index = index
  }

  private getOptionByValue (value: Value | null) {
    if (!this.options) {
      return -1
    }
    return this.options.findIndex((option) => option.value === value)
  }

  selectHighlighted = () => {
    const { index } = this.highlight
    const option = this.options[index]
    if (!option) {
      return
    }

    this.patchValue(option.value)
  }

}
